/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { DropdownQuantity } from '../../../utilities/dropdown-quantity';

import {
    IBuyboxProps as IBuyboxExtentionProps,
    IBuyboxResources as IBuyboxExtentionResources
} from '../definition-extensions/buybox.ext.props.autogenerated';
import MsDyn365 from '@msdyn365-commerce/core';
import { ProductDimensionValue, ProductDimensionValueInventoryAvailability } from '@msdyn365-commerce/retail-proxy';
import { IProductDimensionsWithAvailabilitiesFull, ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import { useEffect, useState } from 'react';

interface IDimensionForSelectedVariant extends ProductDimensionFull, IProductDimensionsWithAvailabilitiesFull {}

const colorRecords: Record<string, string> = {};
const colorInStock: string[] = [];
const _strikeSVG =
    'data:image/svg+xml;utf8,' +
    "<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'>" +
    "<path d='M0 0 L100 100 ' stroke='white' stroke-width='5.5'/></svg>";

const updateSwatchTitles = (dimensions: IDimensionForSelectedVariant[]) => {
    const dimensionValues = dimensions.map(d => d.DimensionValues);
    const dimensionValuesFlat: ProductDimensionValue[] = [];
    // @ts-ignore This comes up in the JS as a mobx array but in the ts it's not. So we have to flatten it against ts rules.
    dimensionValues.forEach(dv => dimensionValuesFlat.push(...dv));
    const swatches = document.querySelectorAll('.msc-swatch-container>button');
    if (swatches && swatches.length) {
        for (let i = 0; i < swatches.length; i++) {
            const title = swatches[i].getAttribute('title');
            if (title) {
                const idx = dimensionValuesFlat.findIndex(dv => dv?.Value === title);
                if (idx > -1) {
                    const extensionProperty = dimensionValuesFlat[idx]?.ExtensionProperties?.find(ep => ep.Key === 'swatch-title');
                    if (extensionProperty) {
                        swatches[i].setAttribute('title', extensionProperty.Value?.StringValue || title);
                    }
                }
            }
        }
    }
};

function checkAvailability(p: ProductDimensionValueInventoryAvailability) {
    return p.PhysicalAvailableInventoryLevelCode === 'AVAIL';
}

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        /* description,*/ findInStore,
        quantity,
        price,
        title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        max,
        shopSimilarDescription,
        unitOfMeasure,
        data
    } = props;
    const [previousSelection, setPreviousSelection] = useState(false);
    useEffect(() => {
        if (document) {
            //For best friends + other carousel items
            const imgCollect2 = document.querySelectorAll<HTMLImageElement>(
                '.msc-product__dimensions img.msc-swatch-container__item__image'
            );
            if (imgCollect2.length > 0) {
                imgCollect2.forEach(node => {
                    // check if colour is completely sold out, if yes, color should remain crossed out
                    if (node.classList.contains('msc-swatch-container__item__disabled')) {
                        const thisColour = node.src;
                        node.src = _strikeSVG;
                        node.style.backgroundImage = "url('" + thisColour + "')";
                        //colorRecords[node.title] === undefined ? '' : "url('" + colorRecords[node.title] + "')";
                        node.style.maxWidth = '20px';
                    }
                    // if (
                    //     node.classList.contains('msc-swatch-container__item__selected') &&
                    //     !node.classList.contains('msc-swatch-container__item__disabled')
                    // ) {
                    //     const thisColour = node.src;
                    //     node.src = thisColour;
                    // }
                });
            }
        }
        /* */
    }, [data]);

    if (MsDyn365.isBrowser) {
        const dimensions = data.productDimensions.result;
        if (dimensions) {
            updateSwatchTitles(dimensions);
            if (dimensions[0].dimensionValuesWithInventory !== undefined) {
                var availableColors = dimensions[0].dimensionValuesWithInventory.filter(checkAvailability);
                availableColors.forEach(c => {
                    if (c.DimensionValue?.Value !== undefined && !colorInStock.includes(c.DimensionValue?.Value)) {
                        colorInStock.push(c.DimensionValue.Value);
                    }
                });
            }
        }

        const colorlabel = document.querySelector('.ms-buybox__dropdown-quantity-label[for="ms-buybox__dropown-quantity-input-1"]');
        const colorswatches = colorlabel?.nextElementSibling;
        var colornamecontainer = document.createElement('span');

        if (colorlabel && colorlabel.children && colorlabel.children.length === 1) {
            colorlabel?.append(colornamecontainer);
            if (colorswatches) {
                colorswatches.addEventListener('click', e => {
                    let target = e.target as HTMLElement;
                    if (
                        target.classList.contains('msc-swatch-container__item') 
                        // &&
                        // !target.classList.contains('msc-swatch-container__item__disabled')
                    ) {
                        let colorname = target.title;
                        colornamecontainer.innerText = `: ${colorname}`;
                    }
                });
            }
        }
        if (colornamecontainer) {
            colornamecontainer.innerText = `: ${props.context.request.query?.color ?? ''}`;
        }
        /* Task 1217 & 1312 */

        const selectedSwatch = document.querySelectorAll<HTMLImageElement>(
            '.ms-buybox__dropdown .msc-swatch-container img.msc-swatch-container__item__selected'
        );
        if (selectedSwatch[0] && colorInStock.includes(selectedSwatch[0].title)) {
            const imgCollect = document.querySelectorAll<HTMLImageElement>(
                '.ms-buybox__dropdown .msc-swatch-container img.msc-swatch-container__item__image'
            );
            if (imgCollect.length > 0) {
                imgCollect.forEach((node, i) => {
                    if (colorRecords[node.title] === undefined && !node.src.includes('svg+xml')) {
                        colorRecords[node.title] = node.src;
                    }
                    // check if colour is completely sold out, if yes, color should remain crossed out
                    let b: boolean = colorInStock.includes(node.title);
                    if (node.classList.contains('msc-swatch-container__item__disabled') || !b) {
                        node.src = _strikeSVG;
                        node.style.backgroundImage =
                            colorRecords[node.title] === undefined ? '' : "url('" + colorRecords[node.title] + "')";
                        node.style.maxWidth = '40px';
                    }
                    if (
                        node.classList.contains('msc-swatch-container__item__selected') &&
                        !node.classList.contains('msc-swatch-container__item__disabled') &&
                        b
                    ) {
                        node.src = colorRecords[node.title];
                    }
                });
            }
            if (colorInStock.length > 0) {
                const inventoryLabel = document.querySelector<HTMLDivElement>('.ms-buybox__inventory-info');
                const selectedTitle = document.querySelector<HTMLDivElement>('.msc-swatch-container__item__selected');
                // hide 'OOS' text when out of stock size is selected
                if (inventoryLabel !== null && selectedTitle !== null) {
                    inventoryLabel.style.display = colorInStock.includes(selectedTitle.title) ? 'none' : 'block';
                }
            }
            const swatchContainers = document.querySelectorAll('.msc-swatch-container');
            if (swatchContainers) {
                for (let i = 0; i < swatchContainers.length; i++) {
                    const children = swatchContainers[i].children;
                    let hasSelected = false;
                    for (let j = 0; j < children.length; j++) {
                        if (children[j] && children[j].classList.contains('msc-swatch-container__item__selected')) {
                            hasSelected = true;
                            break;
                        }
                    }
                    if (!hasSelected) {
                        // page loaded for first time, if swatch hasn't been selected: select the first available OR first
                        let childToSelect = children[0] as HTMLElement;
                        for (let j = 0; j < children.length; j++) {
                            if (!children[j].classList.contains('msc-swatch-container__item__disabled')) {
                                childToSelect = children[j] as HTMLElement;
                                hasSelected = true;
                                break;
                            }
                        }
                        childToSelect.click();
                    } else if (!previousSelection) {
                        const sizeSwatches = document.querySelectorAll<HTMLImageElement>(
                            '.ms-buybox__dropdown .msc-swatch-container button.msc-swatch-container__item'
                        );

                        // 1312 - size swatches fix
                        let sizeSelected = false;
                        if (sizeSwatches.length > 0) {
                            sizeSwatches.forEach(size => {
                                if (
                                    size.classList.contains('msc-swatch-container__item__selected') &&
                                    !size.classList.contains('msc-swatch-container__item__disabled')
                                ) {
                                    sizeSelected = true;
                                }
                            });
                            if (!sizeSelected) {
                                let clicked = false;
                                sizeSwatches.forEach(size => {
                                    if (!size.classList.contains('msc-swatch-container__item__disabled') && !clicked) {
                                        size.click();
                                        clicked = true;
                                        setPreviousSelection(true);
                                    }
                                });
                            }
                        }
                    }
                }
            }
        } else {
            const imgCollect = document.querySelectorAll<HTMLImageElement>(
                '.ms-buybox__dropdown .msc-swatch-container img.msc-swatch-container__item__image'
            );
            if (imgCollect.length > 0) {
                imgCollect.forEach(node => {
                    if (colorRecords[node.title] === undefined && !node.src.includes('svg+xml')) {
                        colorRecords[node.title] = node.src;
                    }
                    // check if colour is completely sold out, if yes, color should remain crossed out
                    let b: boolean = colorInStock.includes(node.title);
                    if (node.classList.contains('msc-swatch-container__item__disabled') || !b) {
                        node.src = _strikeSVG;
                        node.style.backgroundImage =
                            colorRecords[node.title] === undefined ? '' : "url('" + colorRecords[node.title] + "')";
                        node.style.maxWidth = '40px';
                    }
                    if (
                        node.classList.contains('msc-swatch-container__item__selected') &&
                        !node.classList.contains('msc-swatch-container__item__disabled') &&
                        b
                    ) {
                        node.src = colorRecords[node.title];
                    }
                });
            }
        }

        /* */
    }

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                <div className='love-title'>
                    {title}
                    {addToWishlist && addToWishlist.button}
                </div>

                {price}
                {unitOfMeasure}
                {addToWishlist && addToWishlist.errorBlock}

                {/*{props.bulkPurchaseLink}*/}
                {/* {description} */}
                <hr className='divide' />
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}

                {inventoryLabel}

                {/* <div className="er">
                    {addToCart && addToCart.errorBlock}
                </div> */}
                <div className='pick-and-add'>
                    {quantity &&
                        _renderQuantity(quantity, callbacks, props, state, resources, quantityLimitsMessages, max, telemetryContent)}
                    {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                </div>

                {_renderTextBlock(props.slots.textBlocks)}
                {props.slots && props.slots.pdpspecs}
                {rating}

                {findInStore && _renderFindInStore(findInStore)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        </Module>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-add-to-cart'>
            {/* {addToCart && addToCart.errorBlock} */}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && addToOrderTemplate.button}
            </div>
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, /* description,*/ errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {/* {description} */}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;
    const { quantity } = state;

    var dropdownOptions: number[] = [];
    if (!max) {
        max = 10;
    }

    for (let i = 1; i <= max; i++) {
        dropdownOptions.push(i);
    }

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <DropdownQuantity
                id='ms-buybox__product-quantity-input'
                max={max}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                options={dropdownOptions}
            />

            {/* <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={max}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            /> */}
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
